import { ArrayOr, UnresolvedAsset, UnresolvedAssetObject } from "pixi.js";
import { BundleResourcesLoader } from "./BundleResourcesLoader";
import { injectable, initializer } from "@launchtray/tsyringe-async";
import { Event } from "event-system";
import { PromiseCompletionSource } from "../shared/async/PromiseCompletionSource";
import { IResourcePackage } from "./IResourcePackage";
import { BundleResourcesLoaderWrapper } from "./BundleResourcesLoaderWrapper";

@injectable()
export class BundleResourcesPackage implements IResourcePackage {
    private static readonly usagesMap: Map<string, number> = new Map();

    public get loaded(): boolean {
        return this.loader.isLoaded;
    }
    public readonly onLoadingProgress: Event<number>;
    private loader: BundleResourcesLoader;
    private alreadyLoading: PromiseCompletionSource<void>;
    private destroyed: boolean;

    public constructor(bundleName: string) {
        this.loader = new BundleResourcesLoaderWrapper(bundleName);
        this.onLoadingProgress = new Event<number>();
        this.handleLoaderProgress = this.handleLoaderProgress.bind(this);
        this.trackUsages();
        this.destroyed = false;
    }

    @initializer()
    public async load(): Promise<BundleResourcesPackage> {
        try {
            if (!this.loaded) {
                if (this.alreadyLoading != undefined) {
                    await this.alreadyLoading.result;
                } else {
                    this.alreadyLoading = new PromiseCompletionSource<void>();
                    this.loader.onProgress.add(this.handleLoaderProgress);
                    await this.loader.load();
                    this.loader.onProgress.remove(this.handleLoaderProgress);
                    this.alreadyLoading.complete();
                }
            }
        }
        catch (e: unknown) {
            console.error("Unable to load bundle, ", e);
        }
        finally {
            return this;
        }
    }

    public resource<T>(name: string): T {
        if (!this.loaded) {
            throw new Error(`Package ${this.constructor.name} not yet loaded.`);
        }
        return this.loader.getResource<T>(name);
    }

    protected updateBundleContent(assets: UnresolvedAsset[] | Record<string, ArrayOr<string> | UnresolvedAssetObject>): void {
        this.loader.updateBundle(assets);
    }

    private handleLoaderProgress(progress: number): void {
        this.onLoadingProgress.emit(progress);
    }

    public destroy(): void {
        const constructorName = Object.getPrototypeOf(this).constructor.name;
        if(this.destroyed) {
            console.warn(`Attemp of double destroy ${constructorName}`);
            return;
        }
        this.destroyed = true;
        let currentUsages = BundleResourcesPackage.usagesMap.get(constructorName);
        BundleResourcesPackage.usagesMap.set(constructorName, --currentUsages);
        if (currentUsages !== 0) {
            return;
        }
        this.alreadyLoading.result.then(() => {
            this.loader.destroy();
        });
    }

    private trackUsages(): void {
        const constructorName = Object.getPrototypeOf(this).constructor.name;
        if (!BundleResourcesPackage.usagesMap.has(constructorName)) {
            BundleResourcesPackage.usagesMap.set(constructorName, 1);
        }
        else {
            let currentUsages = BundleResourcesPackage.usagesMap.get(constructorName);
            BundleResourcesPackage.usagesMap.set(constructorName, ++currentUsages);
        }
    }
}
